import DownloadOva from '@bumble/app/components/DownloadOva';
import GenerateCloudData from '@bumble/app/components/GenerateCloudData';
import HardwareDevice from '@bumble/app/components/HardwareDevice';
import HostedNode from '@bumble/app/components/HostedNode';
import { Loading } from '@bumble/app/components/Loading';
import NodeComplete from '@bumble/app/components/NodeComplete';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

const formFactorOptions = [
  {
    label: 'Download OVA',
    value: 'download',
  },
  {
    label: 'Hardware Device',
    value: 'hardware',
  },
  {
    label: 'AWS AMI',
    value: 'aws',
  },
  {
    label: 'Hosted Node',
    value: 'hosted',
  },
  {
    label: 'Skip for now',
    value: 'skip',
  },
];

interface EndpointNodeCreateProps {
  onCancel: () => void;
  showExisting?: boolean;
}
export const EndpointNodeCreate = ({ onCancel, showExisting = true }: EndpointNodeCreateProps) => {
  const api = useApi();

  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePrev = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const [endpointGroups, setEndpointGroups] = useState('new');
  const handleEndpointGroupChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setEndpointGroups(value);
  };

  const [endpointNodeName, setEndpointNodeName] = React.useState('');
  const [endpointNodeGroup, setEndpointNodeGroup] = React.useState('');
  const [selectedEndpointNodeGroup, setSelectedEndpointNodeGroup] = React.useState('');
  const [selectedNode, setSelectedNode] = React.useState('');
  const [selectedFormFactor, setSelectedFormFactor] = React.useState(formFactorOptions[3].value);

  const enPost = useMutation(api.createEpNodeApiV1CreateEndpointNodePost, {
    onSuccess(data) {
      toast.success('Endpoint Node created.');
      setSelectedNode(data?.data?.ep_node_id);
      handleNext();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    if (endpointGroups === 'new') {
      enPost.mutate({
        ep_node_name: endpointNodeName,
        ep_node_group_name: endpointNodeGroup,
        new_ep_node_group: true,
      });
    } else {
      enPost.mutate({
        ep_node_name: endpointNodeName,
        ep_node_group_id: selectedEndpointNodeGroup,
        new_ep_node_group: false,
      });
    }
  };

  const handleCancel = () => {
    setSelectedNode('');
    onCancel();
  };

  const { isLoading: groupIsLoading, data: groupData } = useQuery(
    ['endpointNodeGroups'],
    api.listEpNodeGroupApiV1ListEndpointNodeGroupGet.bind(null, {}),
    { enabled: true },
  );

  if (groupIsLoading) {
    return <Loading />;
  }

  return (
    <Box pt={2}>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>Create Endpoint Node</StepLabel>
          <StepContent>
            <Box>
              <TextField
                fullWidth
                id="ep-name"
                label="Endpoint Node Name"
                variant="outlined"
                sx={{ mb: 4 }}
                value={endpointNodeName}
                onChange={(e) => setEndpointNodeName(e.target.value)}
                autoFocus
              />
              {showExisting && groupData !== undefined && groupData?.data.length > 0 && (
                <>
                  <FormControl fullWidth sx={{ mb: 4 }}>
                    <FormLabel id="endpoint-location">Endpoint Node Group</FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="endpoint-location"
                      name="endpoint-group"
                      onChange={handleEndpointGroupChange}
                      value={endpointGroups}
                    >
                      <FormControlLabel value="new" control={<Radio />} label="New Group" />
                      <FormControlLabel value="existing" control={<Radio />} label="Use Existing" />
                    </RadioGroup>
                  </FormControl>
                  <Collapse in={endpointGroups === 'existing'} timeout="auto">
                    <FormControl fullWidth sx={{ mt: 4, mb: 2 }}>
                      <InputLabel id="node-groups-label">Endpoint Node Groups</InputLabel>
                      <Select
                        labelId="node-groups-label"
                        id="node_groups"
                        label="Endpoint Node Groups"
                        value={selectedEndpointNodeGroup}
                        onChange={(e) => setSelectedEndpointNodeGroup(e.target.value)}
                      >
                        {groupData.data.map(
                          ({ ep_node_group_name, ep_node_group_id }) => (
                            <MenuItem value={ep_node_group_id} key={ep_node_group_id}>
                              {ep_node_group_name}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </Collapse>
                </>
              )}
              <Collapse in={endpointGroups === 'new'} timeout="auto">
                <TextField
                  fullWidth
                  id="epg-name"
                  label="Endpoint Node Group Name"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={endpointNodeGroup}
                  onChange={(e) => setEndpointNodeGroup(e.target.value)}
                />
              </Collapse>
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
                <Button variant="outlined" onClick={handleSubmit}>
                  Create Endpoint Node
                </Button>
              </Box>
            </Box>
          </StepContent>
        </Step>
        <Step>
          <StepLabel>Node Form Factor</StepLabel>
          <StepContent>
            <>
              <FormControl fullWidth sx={{ mb: 4 }}>
                <FormLabel id="node-form">Select an option</FormLabel>
                <RadioGroup
                  aria-labelledby="node-form"
                  name="nodeform"
                  onChange={(e) => setSelectedFormFactor(e.target.value)}
                  value={selectedFormFactor}
                >
                  {formFactorOptions.map(({ label, value }) => (
                    <FormControlLabel value={value} control={<Radio />} label={label} key={value} />
                  ))}
                </RadioGroup>
              </FormControl>
              <Button variant="outlined" onClick={handleNext}>
                Continue
              </Button>
            </>
          </StepContent>
        </Step>
        <Step>
          <StepLabel>Finish {selectedFormFactor !== 'skip' && `(${formFactorOptions.filter((f) => f.value === selectedFormFactor)[0].label})`}</StepLabel>
          <StepContent>
            {selectedFormFactor === 'download' && selectedNode !== '' && (
              <DownloadOva
                type="endpoint"
                node={selectedNode}
                onClose={handleCancel}
                secondaryBtn={handlePrev}
              />
            )}
            {selectedFormFactor === 'hardware' && selectedNode !== '' && (
              <HardwareDevice
                type="endpoint"
                node={selectedNode}
                onClose={handleCancel}
                secondaryBtn={handlePrev}
              />
            )}
            {selectedFormFactor === 'aws' && selectedNode !== '' && (
              <GenerateCloudData
                type="endpoint"
                node={selectedNode}
                onClose={handleCancel}
                secondaryBtn={handlePrev}
              />
            )}
            {selectedFormFactor === 'hosted' && selectedNode !== '' && (
              <HostedNode
                type="endpoint"
                node={selectedNode}
                onClose={handleCancel}
                secondaryBtn={handlePrev}
              />
            )}
            {selectedFormFactor === 'skip' && (
              <NodeComplete onClose={handleCancel} secondaryBtn={handlePrev} />
            )}
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
};
